import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminNotifications = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    is_read: '',
    is_sent: '',
    type: '',
    date_from: '',
    date_to: '',
    ...initialFilters
  });

  const readStatusOptions = [
    { value: '', label: 'All Notifications' },
    { value: 'true', label: 'Read' },
    { value: 'false', label: 'Unread' }
  ];

  const sentStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'true', label: 'Sent' },
    { value: 'false', label: 'Pending' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: notificationsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['adminNotifications', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/notifications${queryString ? `?${queryString}` : ''}`);
      return response.data;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/notifications', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminNotifications']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/notifications/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminNotifications']);
    }
  });

  const notifications = useMemo(() => {
    return notificationsResponse?.data?.data || notificationsResponse?.data || [];
  }, [notificationsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(notification => 
      notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const statistics = useMemo(() => {
    const totalNotifications = notifications.length;
    const unreadNotifications = notifications.filter(n => !n.is_read).length;
    const sentNotifications = notifications.filter(n => n.is_sent).length;

    return {
      totalNotifications,
      unreadNotifications,
      sentNotifications
    };
  }, [notifications]);

  return {
    notifications,
    isLoading,
    error,
    filters,
    readStatusOptions,
    sentStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    createNotification: createMutation.mutate,
    isCreating: createMutation.isPending,
    deleteNotification: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    refetch
  };
};

